/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { forwardRef } from 'react';
import { Empty } from 'antd';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { getContractsAndVouchersProjectSide } from '~src/services/api/contracts';
import { renderOptionalVoucherBadge } from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/utils';

export type TenderContractPrintProps = {
  projectId: string;
  tenderId: string;
  isStartFetch?: boolean;
};

type Voucher = {
  id: number;
  name: string;
  description?: string;
  is_optional?: boolean;
  is_voucher_group: boolean;
  sort: number;
  voucher_group_option?: {
    sub_voucher_ids: number[];
  };
};

type Contract = {
  name: string;
  description?: string;
  reference?: string;
  vouchers: Voucher[];
};

const TenderContractPrint = forwardRef<
  HTMLDivElement,
  TenderContractPrintProps
>(({ projectId, tenderId, isStartFetch }, ref) => {
  const intl = useIntl();

  const { data: contracts, isLoading } = useQuery<Contract[]>(
    ['contracts-print', projectId, tenderId],
    () =>
      getContractsAndVouchersProjectSide({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
      }),
    { enabled: Boolean(isStartFetch) }
  );

  if (!isStartFetch || isLoading) return null;

  if (!contracts || contracts.length === 0) {
    return (
      <Empty
        description={intl.formatMessage({ id: 'no-contracts' })}
        css={{ padding: '5vh 0', margin: 0 }}
      />
    );
  }

  const getContractTitle = (contract: Contract) => {
    if (contract.reference) return `${contract.reference} — ${contract.name}`;
    return contract.name;
  };

  const wrapFixCss = {
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const,
    whiteSpace: 'pre-wrap' as const,
  };

  // Styles shared across the doc
  const textMuted = '#666';
  const borderLight = '#e8e8e8';

  return (
    <div
      ref={ref}
      css={{
        // Document base
        padding: '24px 32px', // Slightly wider for document feel
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize: '12px', // Slightly smaller for professional print density
        lineHeight: 1.5,
        color: '#000', // Pure black for print contrast
        background: '#fff',

        // Print setup
        '@media print': {
          padding: '0.5cm',
          fontSize: '11px', // Save paper
        },
      }}>
      {/* Document Header */}
      <div
        css={{
          marginBottom: '24px',
          paddingBottom: '12px',
          borderBottom: '2px solid #000',
        }}>
        <div
          css={{ fontSize: '20px', fontWeight: 800, letterSpacing: '-0.5px' }}>
          {intl.formatMessage({ id: 'contract-print-title' })}
        </div>
        <div css={{ fontSize: '13px', color: textMuted, marginTop: '2px' }}>
          {intl.formatMessage({ id: 'contract-print-subtitle' })}
        </div>
      </div>

      {contracts.map((contract, contractIdx) => {
        const groups = (contract.vouchers ?? []).filter(
          (v) => v.is_voucher_group
        );
        const cards = (contract.vouchers ?? []).filter(
          (v) => !v.is_voucher_group
        );

        const cardMap = new Map(cards.map((c) => [c.id, c]));

        const groupedCardIds = new Set<number>();
        groups.forEach((g) =>
          (g.voucher_group_option?.sub_voucher_ids ?? []).forEach((id) =>
            groupedCardIds.add(id)
          )
        );

        const ungroupedCards = cards
          .filter((c) => !groupedCardIds.has(c.id))
          .sort((a, b) => a.sort - b.sort);

        const renderVoucherRow = (v: Voucher, isLast: boolean) => {
          return (
            <div
              key={v.id}
              css={{
                padding: '6px 0',
                borderBottom: isLast ? 'none' : `1px dashed ${borderLight}`,
                pageBreakInside: 'avoid',
                breakInside: 'avoid',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
              }}>
              <div
                css={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '6px',
                  flex: 1,
                  minWidth: 0,
                }}>
                {v.is_optional && (
                  <span
                    css={{
                      flex: '0 0 auto',
                      marginTop: '1px',
                    }}>
                    {renderOptionalVoucherBadge(intl)}
                  </span>
                )}

                <span
                  css={{
                    fontWeight: 600,
                    fontSize: '12px',
                    color: '#222',
                    flex: '1 1 auto',
                    minWidth: 0,
                    ...wrapFixCss,
                  }}>
                  {v.name}
                </span>
              </div>

              <div
                css={{
                  color: v.description ? textMuted : '#aaa',
                  textAlign: 'right',
                  fontSize: '11px',
                  maxWidth: '60%',
                  fontStyle: v.description ? 'normal' : 'italic',
                  ...wrapFixCss,
                }}>
                {v.description ||
                  intl.formatMessage({ id: 'contract-print-no-description' })}
              </div>
            </div>
          );
        };

        const renderGroup = (group: Voucher) => {
          const subCards = (group.voucher_group_option?.sub_voucher_ids ?? [])
            .map((id) => cardMap.get(id))
            .filter((x): x is Voucher => Boolean(x))
            .sort((a, b) => a.sort - b.sort);

          if (subCards.length === 0) return null;

          return (
            <section
              key={group.id}
              css={{
                marginTop: '12px',
                pageBreakInside: 'avoid',
                breakInside: 'avoid',
                paddingLeft: '12px',
                borderLeft: '3px solid #ddd', // Replaces the box with a clean indicator
              }}>
              {/* Group header */}
              <div
                css={{
                  paddingBottom: '4px',
                  borderBottom: `1px solid ${borderLight}`,
                  marginBottom: '4px',
                }}>
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '6px',
                    minWidth: 0,
                  }}>
                  {group.is_optional && (
                    <span
                      css={{
                        flex: '0 0 auto',
                        marginTop: '1px',
                      }}>
                      {renderOptionalVoucherBadge(intl)}
                    </span>
                  )}

                  <span
                    css={{
                      fontWeight: 800,
                      fontSize: '12px',
                      flex: '1 1 auto',
                      minWidth: 0,
                      ...wrapFixCss,
                    }}>
                    {group.name}
                  </span>
                </div>
                {group.description && (
                  <div
                    css={{
                      fontSize: '11px',
                      color: textMuted,
                      ...wrapFixCss,
                    }}>
                    {group.description}
                  </div>
                )}
              </div>

              {/* Group items */}
              <div>
                {subCards.map((v, idx) =>
                  renderVoucherRow(v, idx === subCards.length - 1)
                )}
              </div>
            </section>
          );
        };

        return (
          <section
            key={contractIdx}
            css={{
              marginTop: '24px',
              // Removed the big border box. Just spacing now.
              pageBreakInside: 'avoid',
              breakInside: 'avoid',
            }}>
            {/* Contract header */}
            <div
              css={{
                paddingBottom: '8px',
                borderBottom: '2px solid #000', // Strong separator
                marginBottom: '8px',
                pageBreakInside: 'avoid',
                breakInside: 'avoid',
              }}>
              <div
                css={{
                  fontSize: '15px',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  ...wrapFixCss,
                }}>
                {getContractTitle(contract)}
              </div>
              {contract.description && (
                <div
                  css={{
                    marginTop: '4px',
                    color: textMuted,
                    fontSize: '12px',
                    ...wrapFixCss,
                  }}>
                  {contract.description}
                </div>
              )}
            </div>

            {/* Content */}
            <div>
              {/* Groups section */}
              {groups.length > 0 && (
                <div css={{ marginTop: '12px' }}>
                  <div
                    css={{
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      color: '#888',
                      marginBottom: '4px',
                    }}>
                    {intl.formatMessage({ id: 'contract-print-groups' })}
                  </div>

                  <div
                    css={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}>
                    {groups.sort((a, b) => a.sort - b.sort).map(renderGroup)}
                  </div>
                </div>
              )}

              {/* Ungrouped section */}
              {ungroupedCards.length > 0 && (
                <div css={{ marginTop: groups.length > 0 ? '20px' : '8px' }}>
                  <div
                    css={{
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      color: '#888',
                      marginBottom: '4px',
                      paddingBottom: '2px',
                      borderBottom: `1px solid ${borderLight}`,
                    }}>
                    {intl.formatMessage({ id: 'contract-ungrouped-vouchers' })}
                  </div>

                  <div>
                    {ungroupedCards.map((v, idx) =>
                      renderVoucherRow(v, idx === ungroupedCards.length - 1)
                    )}
                  </div>
                </div>
              )}

              {/* If contract has no vouchers at all */}
              {groups.length === 0 && ungroupedCards.length === 0 && (
                <div
                  css={{
                    marginTop: '12px',
                    color: textMuted,
                    fontStyle: 'italic',
                  }}>
                  {intl.formatMessage({ id: 'contract-print-empty-contract' })}
                </div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
});

export default TenderContractPrint;
